/**
 * HR Employee Flags page — /hr/employees/:employeeId/flags
 *
 * Lists active (and optionally resolved) flags for one employee.
 * Each flag row shows: humanised flag_code, severity chip, source chip,
 * reason, created_at, and a Resolve button.
 *
 * Resolve opens a modal requiring a non-empty resolution_note.
 *
 * HR / Director / ExecDirector only — HrAccessGuard enforces the role client-
 * side; the API enforces it server-side.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ConfigurePatternLink } from '../components/hr/ConfigurePatternLink'
import { HrAccessGuard } from '../components/hr/HrAccessGuard'
import { Badge } from '../components/primitives/Badge'
import { Modal } from '../components/primitives/Modal'
import { PageHead } from '../components/primitives/PageHead'
import {
  createEmployeeFlag,
  humaniseFlagCode,
  listEmployeeFlags,
  resolveEmployeeFlag,
  VALID_FLAG_CODES,
  FLAG_CODE_DEFAULT_SEVERITY,
  type EmployeeFlag,
  type FlagCode,
  type FlagSeverity,
} from '../api/employee-flags'
import { getEmployee } from '../api/employees'
import { ApiError } from '../api/client'
import { fmtDate } from '../lib/format'

// ── Style helpers ─────────────────────────────────────────────────────────────

function inputStyle(): React.CSSProperties {
  return {
    padding: '8px 10px',
    border: '1px solid var(--border)',
    borderRadius: 8,
    background: 'var(--surface)',
    fontSize: 13,
    fontFamily: 'inherit',
    width: '100%',
    boxSizing: 'border-box',
  }
}

function ghostButton(): React.CSSProperties {
  return {
    padding: '6px 12px',
    background: 'var(--surface)',
    color: 'var(--text-2)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 12,
    cursor: 'pointer',
  }
}

function primaryButton(disabled = false): React.CSSProperties {
  return {
    padding: '8px 14px',
    background: disabled ? 'var(--border-strong)' : 'var(--steel-blue)',
    color: 'var(--surface)',
    border: 'none',
    borderRadius: 8,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
  }
}

function severityVariant(s: FlagSeverity): 'danger' | 'warning' | 'info' {
  if (s === 'high') return 'danger'
  if (s === 'medium') return 'warning'
  return 'info'
}

function sourceLabel(src: string): string {
  if (src === 'ai_pattern') return 'AI Pattern'
  if (src === 'ai_reason_judge') return 'AI Judge'
  if (src === 'hr_manual') return 'HR Manual'
  return src
}

function formatDate(iso: string): string {
  return fmtDate(iso)
}

// ── Page ──────────────────────────────────────────────────────────────────────

interface Props {
  employeeId: string
}

export function HrEmployeeFlagsPage({ employeeId }: Props): JSX.Element {
  const queryClient = useQueryClient()
  const [includeResolved, setIncludeResolved] = useState(false)
  const [resolvingFlag, setResolvingFlag] = useState<EmployeeFlag | null>(null)
  const [resolveNote, setResolveNote] = useState('')
  const [resolveError, setResolveError] = useState<string | null>(null)
  const [addingFlag, setAddingFlag] = useState(false)

  const employeeQuery = useQuery({
    queryKey: ['employee', employeeId],
    queryFn: () => getEmployee(employeeId),
  })

  const flagsQuery = useQuery({
    queryKey: ['employee-flags', employeeId, includeResolved],
    queryFn: () => listEmployeeFlags(employeeId, includeResolved),
  })

  const resolveMutation = useMutation({
    mutationFn: (flag: EmployeeFlag) =>
      resolveEmployeeFlag(employeeId, flag.id, { resolutionNote: resolveNote }),
    onSuccess: () => {
      setResolvingFlag(null)
      setResolveNote('')
      setResolveError(null)
      void queryClient.invalidateQueries({ queryKey: ['employee-flags', employeeId] })
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setResolveError(`${err.message} (${err.status})`)
      } else if (err instanceof Error) {
        setResolveError(err.message)
      } else {
        setResolveError('Failed to resolve flag')
      }
    },
  })

  const employeeName = employeeQuery.data?.name ?? 'Employee'
  const flags = flagsQuery.data ?? []

  function openResolve(flag: EmployeeFlag): void {
    setResolvingFlag(flag)
    setResolveNote('')
    setResolveError(null)
  }

  function handleResolveSubmit(e: React.FormEvent): void {
    e.preventDefault()
    if (!resolvingFlag) return
    if (!resolveNote.trim()) {
      setResolveError('Resolution note is required')
      return
    }
    resolveMutation.mutate(resolvingFlag)
  }

  return (
    <HrAccessGuard>
      <PageHead
        eyebrow="HR"
        title={`${employeeName} — Flags`}
        subtitle="Observational flags raised by AI detectors or HR staff."
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}
            >
              <input
                type="checkbox"
                checked={includeResolved}
                onChange={(e) => setIncludeResolved(e.target.checked)}
              />
              Show resolved
            </label>
            <button
              type="button"
              data-testid="add-flag-button"
              onClick={() => setAddingFlag(true)}
              style={{
                padding: '8px 14px',
                borderRadius: 10,
                border: 'none',
                background: 'var(--steel-blue)',
                color: 'var(--surface)',
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              + Add flag
            </button>
          </div>
        }
      />

      {flagsQuery.isLoading ? (
        <p style={{ color: 'var(--text-3)', fontSize: 13 }}>Loading flags…</p>
      ) : flagsQuery.isError ? (
        <p style={{ color: 'var(--danger-fg)', fontSize: 13 }}>
          Failed to load flags.{' '}
          <button
            type="button"
            style={ghostButton()}
            onClick={() => void flagsQuery.refetch()}
          >
            Retry
          </button>
        </p>
      ) : flags.length === 0 ? (
        <div
          style={{
            padding: '32px 24px',
            textAlign: 'center',
            color: 'var(--text-3)',
            border: '1px dashed var(--border)',
            borderRadius: 12,
          }}
        >
          <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>No flags.</p>
          <p style={{ fontSize: 13 }}>
            {includeResolved
              ? 'No flags recorded for this employee.'
              : 'No active flags. Toggle "Show resolved" to see history.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {flags.map((flag) => (
            <FlagRow
              key={flag.id}
              flag={flag}
              onResolve={() => openResolve(flag)}
            />
          ))}
        </div>
      )}

      {/* Resolve modal */}
      <Modal
        open={Boolean(resolvingFlag)}
        onClose={() => setResolvingFlag(null)}
        title={resolvingFlag ? `Resolve — ${humaniseFlagCode(resolvingFlag.flagCode)}` : ''}
      >
        {resolvingFlag ? (
          <form onSubmit={handleResolveSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ fontSize: 13, color: 'var(--text-2)', margin: 0 }}>
              Reason: {resolvingFlag.reason}
            </p>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>
                Resolution note (required)
              </span>
              <textarea
                data-testid="resolution-note-input"
                value={resolveNote}
                onChange={(e) => setResolveNote(e.target.value)}
                rows={3}
                placeholder="Describe how this was addressed…"
                style={{ ...inputStyle(), resize: 'vertical' }}
              />
            </label>
            {resolveError ? (
              <p
                role="alert"
                style={{ color: 'var(--danger-fg)', fontSize: 13, margin: 0 }}
              >
                {resolveError}
              </p>
            ) : null}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setResolvingFlag(null)}
                disabled={resolveMutation.isPending}
                style={ghostButton()}
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="resolve-flag-submit"
                disabled={resolveMutation.isPending}
                style={primaryButton(resolveMutation.isPending)}
              >
                {resolveMutation.isPending ? 'Resolving…' : 'Mark resolved'}
              </button>
            </div>
          </form>
        ) : null}
      </Modal>

      {/* Add flag modal */}
      {addingFlag ? (
        <AddFlagModal
          employeeId={employeeId}
          onClose={() => setAddingFlag(false)}
          onSaved={() => {
            setAddingFlag(false)
            void queryClient.invalidateQueries({ queryKey: ['employee-flags', employeeId] })
          }}
        />
      ) : null}
    </HrAccessGuard>
  )
}

// ── FlagRow ───────────────────────────────────────────────────────────────────

interface FlagRowProps {
  flag: EmployeeFlag
  onResolve: () => void
}

function FlagRow({ flag, onResolve }: FlagRowProps): JSX.Element {
  return (
    <div
      data-testid="flag-row"
      style={{
        border: '1px solid var(--border)',
        borderRadius: 10,
        padding: '12px 16px',
        background: flag.resolvedAt ? 'var(--surface-2)' : 'var(--surface)',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', flex: '1 1 auto' }}>
          {humaniseFlagCode(flag.flagCode)}
        </span>
        <Badge variant={severityVariant(flag.severity)}>
          {flag.severity.charAt(0).toUpperCase() + flag.severity.slice(1)}
        </Badge>
        <Badge variant="default">{sourceLabel(flag.source)}</Badge>
        {flag.tags?.includes('perf_review') ? (
          <Badge variant="warning">Perf review</Badge>
        ) : null}
        {flag.resolvedAt ? (
          <Badge variant="success">Resolved</Badge>
        ) : null}
        {flag.flagCode.startsWith('pattern_') ? (
          <ConfigurePatternLink code={flag.flagCode.replace(/^pattern_/, '')} />
        ) : null}
      </div>

      <p style={{ fontSize: 13, color: 'var(--text-2)', margin: 0 }}>{flag.reason}</p>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
          Raised {formatDate(flag.createdAt)}
          {flag.resolvedAt ? ` · Resolved ${formatDate(flag.resolvedAt)}` : ''}
          {flag.resolutionNote ? ` · "${flag.resolutionNote}"` : ''}
        </span>
        {!flag.resolvedAt ? (
          <button
            type="button"
            data-testid="resolve-flag-button"
            onClick={onResolve}
            style={ghostButton()}
          >
            Resolve
          </button>
        ) : null}
      </div>
    </div>
  )
}

// ── AddFlagModal ──────────────────────────────────────────────────────────────

interface AddFlagModalProps {
  employeeId: string
  onClose: () => void
  onSaved: () => void
}

function AddFlagModal({ employeeId, onClose, onSaved }: AddFlagModalProps): JSX.Element {
  const [flagCode, setFlagCode] = useState<FlagCode>('hr_custom')
  const [severity, setSeverity] = useState<FlagSeverity>(
    FLAG_CODE_DEFAULT_SEVERITY['hr_custom'],
  )
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const createMutation = useMutation({
    mutationFn: () =>
      createEmployeeFlag(employeeId, { flagCode, severity, reason }),
    onSuccess: onSaved,
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(`${err.message} (${err.status})`)
      } else if (err instanceof Error) {
        setError(err.message)
      } else {
        setError('Failed to create flag')
      }
    },
  })

  function handleFlagCodeChange(code: FlagCode): void {
    setFlagCode(code)
    setSeverity(FLAG_CODE_DEFAULT_SEVERITY[code])
  }

  function onSubmit(e: React.FormEvent): void {
    e.preventDefault()
    setError(null)
    if (!reason.trim()) {
      setError('Reason is required')
      return
    }
    createMutation.mutate()
  }

  return (
    <Modal open onClose={onClose} title="Add employee flag">
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>Flag type</span>
          <select
            data-testid="flag-code-select"
            value={flagCode}
            onChange={(e) => handleFlagCodeChange(e.target.value as FlagCode)}
            style={inputStyle()}
          >
            {VALID_FLAG_CODES.map((code) => (
              <option key={code} value={code}>
                {humaniseFlagCode(code)}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>Severity</span>
          <select
            data-testid="flag-severity-select"
            value={severity}
            onChange={(e) => setSeverity(e.target.value as FlagSeverity)}
            style={inputStyle()}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>
            Reason (required)
          </span>
          <textarea
            data-testid="flag-reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Describe why this flag is being raised…"
            style={{ ...inputStyle(), resize: 'vertical' }}
          />
        </label>

        {error ? (
          <p
            role="alert"
            style={{ color: 'var(--danger-fg)', fontSize: 13, margin: 0 }}
          >
            {error}
          </p>
        ) : null}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={createMutation.isPending}
            style={ghostButton()}
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="add-flag-submit"
            disabled={createMutation.isPending}
            style={primaryButton(createMutation.isPending)}
          >
            {createMutation.isPending ? 'Saving…' : 'Add flag'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
